import type { CustomSlide, Slide, SlideElement } from 'src/content/screens/slide-element';
import { slideSuggestionsCategorized } from 'src/content/screens/slide-suggestions';

// Pre-flatten alle custom-forslag én gang ved import — listen er statisk og
// brukes i hver render av forslags-dialogen.
//
// Bruker bevisst den ufiltrerte `slideSuggestionsCategorized` (ikke den
// audience-filtrerte `slideSuggestionsCategorizedFor`): her sjekker vi om et
// lagret lysbilde i det hele tatt _er_ et kjent standardforslag, uavhengig av
// organisasjonstype. Filtrering på lege/ikke-lege hører ikke hjemme her.
const customSuggestions: CustomSlide[] = slideSuggestionsCategorized
  .flatMap((c) => c.slides)
  .filter((s: Slide): s is CustomSlide => s.type === 'custom');

/**
 * Stripper id-felter fra layout-treet. Server gir saved-slides UUID-er på hver
 * SlideElement (laget av d-edit-slide.addId() før første save), mens forslagene
 * i `slide-suggestions.ts` ikke har id. Direkte JSON-sammenligning ville derfor
 * alltid feile — vi normaliserer bort id før diff.
 */
function stripLayoutIds(el: SlideElement | undefined): unknown {
  if (!el) return undefined;
  const { id: _id, ...rest } = el as SlideElement & { id?: string };
  if (rest.type === 'divider') {
    return {
      ...rest,
      first: stripLayoutIds((rest as { first?: SlideElement }).first),
      last: stripLayoutIds((rest as { last?: SlideElement }).last),
    };
  }
  return rest;
}

/**
 * Strukturell likhet for custom-slides. Vi sammenligner kun innholds-feltene
 * brukeren faktisk redigerer (`header`, `text`, `media`, `layout`) — ikke
 * `id`/`name`/`duration` som styres av editor-state og library-item-rammen.
 *
 * Brukes til å filtrere bort forslagsmaler fra "Mine lysbilder"-kategorien
 * så lenge brukeren ikke har endret dem (#12768).
 */
export function isSameCustomSlide(a: CustomSlide, b: CustomSlide): boolean {
  return (
    a.header === b.header &&
    a.text === b.text &&
    (a.media ?? '') === (b.media ?? '') &&
    JSON.stringify(stripLayoutIds(a.layout)) === JSON.stringify(stripLayoutIds(b.layout))
  );
}

/**
 * Sjekker om en saved-slide er en uendret kopi av et standard-forslag. En slide
 * blir kun "egen" når brukeren har endret minst ett innholds-felt.
 */
export function isUserSlideMatchingSuggestion(slide: CustomSlide): boolean {
  return customSuggestions.some((s) => isSameCustomSlide(slide, s));
}
